import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { IconPenTool, IconTrash } from '../components/Icons';
import { FileText, Copy, Check, TrendingUp, BarChart3, Briefcase, Newspaper, PenLine, Edit3, Save, Eye, Download, Globe, Share2, Plus, X } from 'lucide-react';
import toast from 'react-hot-toast';
// @ts-ignore
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, ReferenceLine, Area, ComposedChart
} from 'recharts';
import {
  getInsightSources, generateInsight, getMyInsights, getInsight, deleteInsight,
  getWritingStyles, updateInsight, refineInsight, createManualDraft, translateInsight,
  addInsightPosition, getInsightPositions
} from '../../services/api';

interface Portfolio { id: string; name: string; }
interface SavedInsight {
  id: string;
  title: string;
  format: string;
  sources_used: string[];
  status: string;
  created_at: string;
}
interface WritingStyle {
  label: string;
  fingerprint: string;
  sample_count?: number;
}

interface ChartSpec {
  type: 'line' | 'bar';
  title: string;
  data: any[];
  lines?: string[];
  colors?: string[];
  dataKey?: string;
  nameKey?: string;
}

// Chart renderer component
function InsightChart({ chart }: { chart: ChartSpec }) {
  if (chart.type === 'line' && chart.lines && chart.data.length > 0) {
    const hasBollinger = chart.lines.includes('Upper') && chart.lines.includes('Lower');

    if (hasBollinger) {
      // Bollinger Band chart — use ComposedChart with Area fill
      const mainLine = chart.lines[0]; // VIX or SPY
      return (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 12px', marginBottom: 16 }}>
          <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', margin: '0 0 12px 4px' }}>{chart.title}</h4>
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={chart.data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} />
              <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} domain={['auto', 'auto']} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
              {/* Bollinger Band fill */}
              <Area type="monotone" dataKey="Upper" stroke="none" fill="#e5e7eb" fillOpacity={0.3} />
              <Area type="monotone" dataKey="Lower" stroke="none" fill="#fff" fillOpacity={1} />
              {/* Band lines */}
              <Line type="monotone" dataKey="Upper" stroke="#d1d5db" strokeWidth={1} strokeDasharray="4 4" dot={false} />
              <Line type="monotone" dataKey="Lower" stroke="#d1d5db" strokeWidth={1} strokeDasharray="4 4" dot={false} />
              {/* SMA20 */}
              <Line type="monotone" dataKey="SMA20" stroke="#94a3b8" strokeWidth={1} strokeDasharray="5 5" dot={false} />
              {/* Main price line */}
              <Line type="monotone" dataKey={mainLine} stroke={chart.colors?.[0] || '#3b82f6'} strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      );
    }

    // Simple line chart (no Bollinger)
    return (
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 12px', marginBottom: 16 }}>
        <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', margin: '0 0 12px 4px' }}>{chart.title}</h4>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chart.data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} />
            <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} domain={['auto', 'auto']} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
            {chart.lines.map((key, i) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={chart.colors?.[i] || '#3b82f6'}
                strokeWidth={key === chart.lines![0] ? 2 : 1}
                dot={false}
                strokeDasharray={i > 0 ? '5 5' : undefined}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (chart.type === 'bar' && chart.data.length > 0) {
    const dataKey = chart.dataKey || 'value';
    const nameKey = chart.nameKey || 'name';
    return (
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 12px', marginBottom: 16 }}>
        <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', margin: '0 0 12px 4px' }}>{chart.title}</h4>
        <ResponsiveContainer width="100%" height={Math.max(200, chart.data.length * 28)}>
          <BarChart data={chart.data} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} />
            <YAxis type="category" dataKey={nameKey} tick={{ fontSize: 11, fill: '#374151' }} width={75} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
            <ReferenceLine x={0} stroke="#d1d5db" />
            <Bar dataKey={dataKey} radius={[0, 4, 4, 0]}>
              {chart.data.map((entry: any, idx: number) => (
                <Cell key={idx} fill={entry[dataKey] >= 0 ? '#10b981' : '#ef4444'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return null;
}

// Preset insight types — each auto-selects the right data sources
type SectorInfo = { etf?: string; name?: string };
type SourceFn = (tickers: string[], portfolioId?: string, sectorInfo?: SectorInfo) => { key: string; params: any }[];

interface InsightPreset {
  key: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  needsTickers?: boolean;
  needsPortfolio?: boolean;
  needsCustom?: boolean;
  needsSectorInfo?: boolean;
  isManual?: boolean;
  sources: SourceFn;
}

const INSIGHT_PRESETS: InsightPreset[] = [
  {
    key: 'stock_analysis',
    label: 'Stock Deep Dive',
    description: 'Fundamentals, technicals, news, and current headlines',
    icon: <TrendingUp size={20} />,
    needsTickers: true,
    sources: (tickers) => [
      { key: 'stocks', params: { tickers } },
      { key: 'news', params: { tickers } },
      { key: 'headlines', params: {} },
    ],
  },
  {
    key: 'sector_deep_dive',
    label: 'Sector Deep Dive',
    description: 'Sector ETF + tickers compared, macro context, earnings, relative performance',
    icon: <BarChart3 size={20} />,
    needsTickers: true,
    needsSectorInfo: true,
    sources: (tickers, _pid, sectorInfo) => [
      { key: 'sector', params: { tickers, sector_etf: sectorInfo?.etf || '', sector_name: sectorInfo?.name || '' } },
      { key: 'market', params: {} },
      { key: 'news', params: { tickers } },
      { key: 'headlines', params: {} },
    ],
  },
  {
    key: 'market_overview',
    label: 'Market Overview',
    description: 'VIX, sectors, technicals + current geopolitical context',
    icon: <BarChart3 size={20} />,
    needsTickers: false,
    sources: () => [
      { key: 'market', params: {} },
      { key: 'headlines', params: {} },
    ],
  },
  {
    key: 'portfolio_review',
    label: 'Portfolio Review',
    description: 'Holdings, P&L, risk metrics, and positioning (private)',
    icon: <Briefcase size={20} />,
    needsTickers: false,
    needsPortfolio: true,
    sources: (_t, portfolioId) => [
      { key: 'portfolio', params: { portfolio_id: portfolioId } },
      { key: 'analytics', params: { portfolio_id: portfolioId } },
      { key: 'headlines', params: {} },
    ],
  },
  {
    key: 'market_and_stocks',
    label: 'Market + Stock Analysis',
    description: 'Market regime + stock analysis + geopolitical context',
    icon: <Newspaper size={20} />,
    needsTickers: true,
    sources: (tickers) => [
      { key: 'market', params: {} },
      { key: 'stocks', params: { tickers } },
      { key: 'news', params: { tickers } },
      { key: 'headlines', params: {} },
    ],
  },
  {
    key: 'custom',
    label: 'Custom / Free-form',
    description: 'Write your own thesis or notes for the AI to develop',
    icon: <PenLine size={20} />,
    needsTickers: false,
    needsCustom: true,
    sources: () => [
      { key: 'custom', params: {} },
      { key: 'headlines', params: {} },
    ],
  },
  {
    key: 'manual',
    label: 'Manual Draft',
    description: 'Write your own essay from scratch — no AI generation',
    icon: <FileText size={20} />,
    needsTickers: false,
    isManual: true,
    sources: () => [],
  },
];

const MODEL_OPTIONS = [
  { key: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.5' },
  { key: 'claude-opus-4-6', label: 'Claude Opus 4.6' },
  { key: 'gpt-4o', label: 'GPT-4o' },
  { key: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
];

const FORMAT_OPTIONS = [
  { key: 'essay', label: 'Long-form Essay' },
  { key: 'linkedin', label: 'LinkedIn Post' },
  { key: 'thread', label: 'Twitter/X Thread' },
  { key: 'summary', label: 'Executive Summary' },
];

export default function InsightsView() {
  const { currentUser } = useAuth();

  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [styles, setStyles] = useState<Record<string, WritingStyle>>({});

  // Selections
  const [selectedPreset, setSelectedPreset] = useState('');
  const [tickerInput, setTickerInput] = useState('');
  const [sectorEtf, setSectorEtf] = useState('');
  const [sectorName, setSectorName] = useState('');
  const [selectedPortfolio, setSelectedPortfolio] = useState('');
  const [customText, setCustomText] = useState('');
  const [selectedFormat, setSelectedFormat] = useState('essay');
  const [selectedModel, setSelectedModel] = useState('claude-sonnet-4-6');
  const [selectedStyle, setSelectedStyle] = useState('');

  // State
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [savedInsights, setSavedInsights] = useState<SavedInsight[]>([]);
  const [viewingInsight, setViewingInsight] = useState<any>(null);
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const editContentRef = useRef('');
  const [saving, setSaving] = useState(false);
  const [showRefine, setShowRefine] = useState(false);
  const [refineText, setRefineText] = useState('');
  const [translating, setTranslating] = useState(false);
  const [refining, setRefining] = useState(false);
  const [showAddPosition, setShowAddPosition] = useState(false);
  const [addingPosition, setAddingPosition] = useState(false);
  const [insightPositions, setInsightPositions] = useState<any[]>([]);

  const getToken = useCallback(async () => {
    if (!currentUser) return null;
    return currentUser.getIdToken();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    const load = async () => {
      try {
        const token = await getToken();
        if (!token) return;

        const [sourcesData, stylesData, insightsData] = await Promise.all([
          getInsightSources(token),
          getWritingStyles(token).catch(() => ({ writing_styles: {} })),
          getMyInsights(token).catch(() => ({ insights: [] })),
        ]);

        // Extract portfolios from sources data
        const portfolioSource = (sourcesData.sources || []).find((s: any) => s.key === 'portfolio');
        if (portfolioSource?.portfolios) {
          setPortfolios(portfolioSource.portfolios);
          if (portfolioSource.portfolios.length > 0) {
            setSelectedPortfolio(portfolioSource.portfolios[0].id);
          }
        }

        setStyles(stylesData.writing_styles || {});
        setSavedInsights(insightsData.insights || []);
      } catch (err: any) {
        toast.error(err.message || 'Failed to load');
      } finally {
        setLoading(false);
      }

      // Deep-link from Track Record or other views
      const navInsightId = localStorage.getItem('aiq_navigate_insight_id');
      if (navInsightId) {
        localStorage.removeItem('aiq_navigate_insight_id');
        try {
          const t = await getToken();
          const data = t ? await getInsight(t, navInsightId) : null;
          if (data) setViewingInsight(data);
        } catch { /* ignore */ }
      }
    };
    load();
  }, [currentUser, getToken]);

  const activePreset = INSIGHT_PRESETS.find(p => p.key === selectedPreset);

  const handleGenerate = async () => {
    const token = await getToken();
    if (!token || !activePreset) return;

    // Manual draft — create blank and open in editor
    if ((activePreset as any).isManual) {
      setGenerating(true);
      try {
        const title = customText.trim() || 'Untitled Draft';
        const result = await createManualDraft(token, title);
        toast.success('Draft created');
        setViewingInsight({ id: result.insight_id, title: result.title, content: '', charts: [] });
        setEditTitle(result.title);
        setEditContent('');
        setIsEditing(true);
        const insightsData = await getMyInsights(token);
        setSavedInsights(insightsData.insights || []);
      } catch (err: any) {
        toast.error(err.message || 'Failed to create draft');
      } finally {
        setGenerating(false);
      }
      return;
    }

    const tickers = tickerInput.split(',').map(t => t.trim().toUpperCase()).filter(Boolean);

    if (activePreset.needsTickers && tickers.length === 0) {
      toast.error('Enter at least one ticker');
      return;
    }
    if ((activePreset as any).needsPortfolio && !selectedPortfolio) {
      toast.error('Select a portfolio');
      return;
    }
    if ((activePreset as any).needsCustom && !customText.trim()) {
      toast.error('Enter your notes or thesis');
      return;
    }

    // Build sources from preset
    let sources = activePreset.sources(tickers, selectedPortfolio, { etf: sectorEtf, name: sectorName });

    // Add custom text if provided (even for non-custom presets)
    if (customText.trim() && selectedPreset !== 'custom') {
      sources = [...sources, { key: 'custom', params: { text: customText.trim() } }];
    }
    if (selectedPreset === 'custom') {
      sources = [{ key: 'custom', params: { text: customText.trim() } }];
      // Also add market context if user wants
      if (tickerInput.trim()) {
        sources.push({ key: 'stocks', params: { tickers } });
      }
    }

    setGenerating(true);
    const toastId = toast.loading('Generating market insight...');

    try {
      const result = await generateInsight(token, {
        sources,
        format: selectedFormat,
        model: selectedModel,
        style_key: selectedStyle || null,
        language: null,
      });

      toast.success('Insight generated!', { id: toastId });
      setViewingInsight({ id: result.insight_id, title: result.title, content: result.content, charts: result.charts || [] });

      const insightsData = await getMyInsights(token);
      setSavedInsights(insightsData.insights || []);
    } catch (err: any) {
      if (err.message?.includes('API key')) {
        toast.error(err.message, { id: toastId, duration: 6000 });
        setSelectedModel('gemini-2.5-flash');
      } else if (err.status === 500 || err.message?.includes('Failed') || err.message?.includes('timeout')) {
        // Backend may still be generating — tell user to refresh
        toast('The insight may still be generating. Click "Refresh" below to check.', { id: toastId, icon: '⏳', duration: 8000 });
        // Try to refresh the list after a delay in case it saved
        setTimeout(async () => {
          try {
            const t = await getToken();
            if (t) {
              const data = await getMyInsights(t);
              setSavedInsights(data.insights || []);
            }
          } catch {}
        }, 5000);
      } else {
        toast.error(err.message || 'Generation failed', { id: toastId });
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleViewInsight = async (id: string) => {
    const token = await getToken();
    if (!token) return;
    try {
      const data = await getInsight(token, id);
      setViewingInsight(data);
    } catch {
      toast.error('Failed to load insight');
    }
  };

  const handleDeleteInsight = async (id: string) => {
    const token = await getToken();
    if (!token) return;
    try {
      await deleteInsight(token, id);
      setSavedInsights(prev => prev.filter(i => i.id !== id));
      if (viewingInsight?.id === id) setViewingInsight(null);
      toast.success('Deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleCopy = () => {
    if (!viewingInsight?.content) return;
    navigator.clipboard.writeText(viewingInsight.content);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePDF = () => {
    if (!viewingInsight) return;
    const content = viewingInsight.content || '';
    const isHtml = content.includes('<p>') || content.includes('<h');
    const bodyHtml = isHtml ? content : content
      .replace(/\\n\\n/g, '\n\n').replace(/\\n/g, '\n')
      .split(/\n\n+/).filter((p: string) => p.trim())
      .map((p: string) => `<p>${p.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')}</p>`)
      .join('\n');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${viewingInsight.title}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: Georgia, serif; margin: 0 auto; color: #111; line-height: 1.7; padding: 16px; max-width: 100%; word-wrap: break-word; overflow-wrap: break-word; }
        h1 { font-size: 1.4rem; margin-bottom: 4px; }
        .meta { color: #666; font-size: 0.85rem; margin-bottom: 24px; }
        p { margin-bottom: 1em; }
        h2, h3 { margin-top: 1.5em; margin-bottom: 0.5em; }
        strong { font-weight: 700; }
        img, svg, table, canvas { max-width: 100% !important; width: auto !important; height: auto !important; overflow: hidden; display: block; margin: 8px 0; }
        img[style] { max-width: 100% !important; width: auto !important; }
        .back-btn { display: inline-block; margin-bottom: 16px; padding: 8px 16px; background: #f3f4f6; border: 1px solid #d1d5db; border-radius: 6px; color: #374151; text-decoration: none; font-size: 0.85rem; font-family: sans-serif; cursor: pointer; }
        footer { margin-top: 40px; font-size: 0.75rem; color: #aaa; border-top: 1px solid #eee; padding-top: 12px; }
        @media (min-width: 768px) { body { max-width: 720px; padding: 30px; } }
        @media print { .back-btn { display: none; } body { padding: 10px; max-width: 100%; } }
      </style></head><body>
      <a class="back-btn" href="javascript:void(0)" onclick="window.close(); setTimeout(function(){window.history.back()},100)">Back / Close</a>
      <h1>${viewingInsight.title}</h1>
      <div class="meta">AILabQuant Market Insights &middot; ${new Date().toLocaleDateString()}</div>
      ${bodyHtml}
      ${(() => {
        // Add chart data as tables for PDF
        const charts = viewingInsight.charts || [];
        if (charts.length === 0) return '';
        return '<h3 style="margin-top:30px;font-size:1rem;color:#374151;">Supporting Data</h3>' +
          charts.map((c: any) => {
            if (c.type === 'bar' && c.data?.length > 0) {
              const rows = c.data.map((d: any) =>
                `<tr><td style="padding:4px 12px 4px 0;border-bottom:1px solid #eee;">${d[c.nameKey || 'name']}</td>` +
                `<td style="padding:4px 0;border-bottom:1px solid #eee;text-align:right;font-weight:600;color:${(d[c.dataKey || 'value'] || 0) >= 0 ? '#059669' : '#dc2626'}">${(d[c.dataKey || 'value'] || 0).toFixed(1)}%</td></tr>`
              ).join('');
              return `<div style="margin:16px 0;"><h4 style="font-size:0.85rem;color:#6b7280;margin-bottom:8px;">${c.title}</h4><table style="width:100%;font-size:0.85rem;border-collapse:collapse;">${rows}</table></div>`;
            }
            return '';
          }).join('');
      })()}
      <footer>Generated by AILabQuant &mdash; ailabquant.com</footer>
    </body></html>`;

    const win = window.open('', '_blank');
    if (!win) { toast.error('Allow pop-ups to export PDF'); return; }
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  const handleShare = async () => {
    if (!viewingInsight?.id) return;
    const url = `https://api.ailabquant.com/api/insights/share/${viewingInsight.id}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: viewingInsight.title, text: viewingInsight.title, url });
      } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    }
  };

  const handleRefine = async () => {
    if (!viewingInsight?.id || !refineText.trim()) return;
    const token = await getToken();
    if (!token) return;

    setRefining(true);
    const toastId = toast.loading('Refining insight with your feedback...');
    try {
      const result = await refineInsight(token, viewingInsight.id, refineText.trim());
      setViewingInsight({ ...viewingInsight, title: result.title, content: result.content });
      setRefineText('');
      setShowRefine(false);
      toast.success('Insight refined!', { id: toastId });
    } catch (err: any) {
      toast.error(err.message || 'Refine failed', { id: toastId });
    } finally {
      setRefining(false);
    }
  };

  const handleTranslate = async (language = 'Chinese') => {
    if (!viewingInsight?.id) return;
    const token = await getToken();
    if (!token) return;

    setTranslating(true);
    const toastId = toast.loading(`Translating to ${language}...`);
    try {
      const result = await translateInsight(token, viewingInsight.id, language);
      toast.success(`Translated! New draft: "${result.title}"`, { id: toastId, duration: 5000 });
      // Refresh the list to show the new translated insight
      const insightsData = await getMyInsights(token);
      setSavedInsights(insightsData.insights || []);
    } catch (err: any) {
      toast.error(err.message || 'Translation failed', { id: toastId });
    } finally {
      setTranslating(false);
    }
  };

  const handlePublish = async () => {
    if (!viewingInsight?.id) return;
    const token = await getToken();
    if (!token) return;
    try {
      await updateInsight(token, viewingInsight.id, {
        title: viewingInsight.title,
        content: viewingInsight.content,
        status: 'published',
      });
      setViewingInsight({ ...viewingInsight, status: 'published' });
      toast.success('Published! Insight is now public.');
    } catch {
      toast.error('Failed to publish');
    }
  };

  useEffect(() => {
    if (viewingInsight?.id) {
      loadInsightPositions(viewingInsight.id);
      setShowAddPosition(false);
    } else {
      setInsightPositions([]);
    }
  }, [viewingInsight?.id]);

  const extractPositionsFromContent = (content: string): { ticker: string; direction: 'long' | 'short'; rationale: string; isOptions: boolean }[] => {
    const knownTickers = new Set([
      'AAPL','MSFT','NVDA','TSLA','AMZN','GOOGL','GOOG','META','JPM','GS',
      'BAC','XOM','CVX','COP','UNH','JNJ','PFE','LLY','BA','CAT',
      'GLD','SLV','USO','GDX','XME','DBA','UNG','COPX','TLT','HYG',
      'XLE','XLK','XLF','XLV','XLI','XLU','XLY','XLB','XLC','XLRE',
      'SPY','QQQ','IWM','DIS','NFLX','CRM','COIN','PLTR','ARM','AMD',
      'INTC','MU','DELL','SMCI','AVGO','TSM','QCOM','TXN','VNQ','VXX','UVXY',
    ]);
    const longWords = /\b(buy|long|add|overweight|rotate into|beneficiar|upside|rally|structural long)\b/i;
    const shortWords = /\b(short|sell|reduce|underweight|avoid|downside|decline|squeeze)\b/i;
    const optionsWords = /\b(straddle|strangle|put spread|call spread|iron condor|collar|protective put|covered call|options?|puts?|calls?|strike|expir)/i;
    const text = content.replace(/<[^>]+>/g, ' '); // strip HTML
    const results: { ticker: string; direction: 'long' | 'short'; rationale: string; isOptions: boolean }[] = [];
    const seen = new Set<string>();

    // Find each ticker and look at surrounding context (100 chars each side)
    const tickerPattern = /\b([A-Z]{2,5})\b/g;
    let match;
    while ((match = tickerPattern.exec(text)) !== null) {
      const ticker = match[1];
      if (!knownTickers.has(ticker) || seen.has(ticker)) continue;
      seen.add(ticker);
      const start = Math.max(0, match.index - 100);
      const end = Math.min(text.length, match.index + ticker.length + 100);
      const context = text.slice(start, end);
      const isOptions = optionsWords.test(context);
      const hasLong = longWords.test(context);
      const hasShort = shortWords.test(context);
      const direction: 'long' | 'short' = hasShort && !hasLong ? 'short' : 'long';
      // Extract a short rationale from the sentence containing the ticker
      const sentenceMatch = context.match(/[^.]*\b[A-Z]{2,5}\b[^.]*/);
      const rationale = sentenceMatch ? sentenceMatch[0].trim().slice(0, 120) : '';
      results.push({ ticker, direction, rationale, isOptions });
    }
    return results;
  };

  const handleQuickAddPosition = async (ticker: string, direction: 'long' | 'short', rationale: string) => {
    if (!viewingInsight?.id) return;
    const token = await getToken();
    if (!token) return;
    setAddingPosition(true);
    try {
      const result = await addInsightPosition(token, viewingInsight.id, {
        ticker, direction, rationale,
      });
      setInsightPositions(prev => [result, ...prev]);
      toast.success(`Added ${result.ticker} @ $${result.entry_price.toFixed(2)}`);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to add position');
    } finally {
      setAddingPosition(false);
    }
  };

  const loadInsightPositions = async (insightId: string) => {
    const token = await getToken();
    if (!token) return;
    try {
      const positions = await getInsightPositions(token, insightId);
      setInsightPositions(positions || []);
    } catch {
      setInsightPositions([]);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <div className="spinner" />
      </div>
    );
  }

  // Convert markdown/raw content to HTML for the editor
  const markdownToHtml = (md: string): string => {
    // If already HTML, return as-is
    if (md.includes('<p>') || md.includes('<h2>') || md.includes('<div>')) {
      return md;
    }
    // Unescape literal \n sequences from JSON
    let text = md.replace(/\\n\\n/g, '\n\n').replace(/\\n/g, '\n');
    // Convert markdown headings
    text = text.replace(/^### (.+)$/gm, '<h3>$1</h3>');
    text = text.replace(/^## (.+)$/gm, '<h2>$1</h2>');
    // Convert bold and italic
    text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    text = text.replace(/\*(.+?)\*/g, '<em>$1</em>');
    // Split into paragraphs: prefer double newlines, fall back to single if no doubles exist
    const hasDoubleNewlines = /\n\n/.test(text);
    const splitter = hasDoubleNewlines ? /\n\n+/ : /\n/;
    return text
      .split(splitter)
      .filter(p => p.trim())
      .map(p => {
        const trimmed = p.trim();
        if (trimmed.startsWith('<h')) return trimmed;
        return `<p>${trimmed}</p>`;
      })
      .join('\n\n');
  };

  const startEditing = () => {
    setEditTitle(viewingInsight.title || '');
    const html = markdownToHtml(viewingInsight.content || '');
    setEditContent(html);
    editContentRef.current = html;
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!viewingInsight?.id) return;
    const token = await getToken();
    if (!token) return;

    setSaving(true);
    try {
      const finalContent = editContentRef.current;
      await updateInsight(token, viewingInsight.id, {
        title: editTitle,
        content: finalContent,
      });
      setViewingInsight({ ...viewingInsight, title: editTitle, content: finalContent });
      setIsEditing(false);
      toast.success('Saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Viewing a single insight
  if (viewingInsight) {
    // Process content for preview mode
    const rawContent = (viewingInsight.content || '')
      .replace(/\\n\\n/g, '\n\n')
      .replace(/\\n/g, '\n');
    const paragraphs = rawContent.split(/\n\n+/).filter((p: string) => p.trim());

    return (
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px 16px', height: '100vh', overflow: 'auto', paddingBottom: 100 }}>
        {/* Sticky toolbar — two rows */}
        <div style={{ position: 'sticky', top: 0, background: '#F0F2F5', paddingTop: 8, paddingBottom: 8, zIndex: 10, marginBottom: 20 }}>
          {/* Row 1: Back + Edit/Save */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <button
              onClick={() => { setViewingInsight(null); setIsEditing(false); }}
              style={{
                background: '#fff', border: '1px solid #d1d5db',
                borderRadius: 8, padding: '8px 16px', cursor: 'pointer', color: '#374151', fontSize: '0.85rem'
              }}
            >
              Back
            </button>
            <div style={{ display: 'flex', gap: 6 }}>
              {isEditing ? (
                <>
                  <button
                    onClick={() => setIsEditing(false)}
                    style={{
                      background: '#fff', border: '1px solid #d1d5db',
                      borderRadius: 8, padding: '7px 12px', cursor: 'pointer', color: '#374151',
                      fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 5
                    }}
                  >
                    <Eye size={14} /> Preview
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{
                      background: '#3b82f6', border: 'none',
                      borderRadius: 8, padding: '7px 12px', cursor: saving ? 'not-allowed' : 'pointer', color: '#fff',
                      fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 5
                    }}
                  >
                    <Save size={14} /> {saving ? 'Saving...' : 'Save'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={startEditing}
                    style={{
                      background: '#fff', border: '1px solid #d1d5db',
                      borderRadius: 8, padding: '7px 12px', cursor: 'pointer', color: '#374151',
                      fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 5
                    }}
                  >
                    <Edit3 size={14} /> Edit
                  </button>
                  <button
                    onClick={() => setShowRefine(!showRefine)}
                    style={{
                      background: showRefine ? '#eff6ff' : '#fff',
                      border: `1px solid ${showRefine ? '#3b82f6' : '#d1d5db'}`,
                      borderRadius: 8, padding: '7px 12px', cursor: 'pointer',
                      color: showRefine ? '#3b82f6' : '#374151',
                      fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 5
                    }}
                  >
                    <PenLine size={14} /> Refine
                  </button>
                  {currentUser?.email && ['dbai999@talkoru.com', 'dbai1089@talkoru.com'].includes(currentUser.email) && (<>
                    <button
                      onClick={() => handleTranslate('Chinese')}
                      disabled={translating}
                      style={{
                        background: '#fff', border: '1px solid #d1d5db',
                        borderRadius: 8, padding: '7px 12px', cursor: translating ? 'not-allowed' : 'pointer',
                        color: '#374151', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 5,
                        opacity: translating ? 0.6 : 1,
                      }}
                    >
                      <Globe size={14} /> {translating ? 'Translating...' : 'Translate to Chinese'}
                    </button>
                    <button
                      onClick={() => setShowAddPosition(!showAddPosition)}
                      style={{
                        background: showAddPosition ? '#f0fdf4' : '#fff',
                        border: `1px solid ${showAddPosition ? '#22c55e' : '#d1d5db'}`,
                        borderRadius: 8, padding: '7px 12px', cursor: 'pointer',
                        color: showAddPosition ? '#16a34a' : '#374151',
                        fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 5,
                      }}
                    >
                      <Plus size={14} /> Track Record
                    </button>
                  </>)}
                </>
              )}
            </div>
          </div>

          {/* Add Position panel (admin only) */}
          {showAddPosition && !isEditing &&
            (() => {
              const extracted = extractPositionsFromContent(viewingInsight.content || '');
              const alreadyAdded = new Set(insightPositions.map((p: any) => p.ticker));
              const available = extracted.filter(p => !alreadyAdded.has(p.ticker));
              return (
                <div style={{
                  background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10,
                  padding: 16, marginBottom: 16,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <p style={{ fontSize: '0.85rem', fontWeight: 600, color: '#16a34a', margin: 0 }}>
                      Add to Track Record
                    </p>
                    <button onClick={() => setShowAddPosition(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                      <X size={14} />
                    </button>
                  </div>
                  {available.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {available.map(pos => pos.isOptions ? (
                        <span
                          key={pos.ticker}
                          title="Options strategy — not trackable yet"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8,
                            padding: '6px 12px', fontSize: '0.8rem', opacity: 0.5, cursor: 'default',
                          }}
                        >
                          <span style={{
                            fontSize: '0.65rem', fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                            background: '#e5e7eb', color: '#6b7280',
                          }}>
                            OPT
                          </span>
                          <span style={{ fontWeight: 600, color: '#6b7280' }}>{pos.ticker}</span>
                        </span>
                      ) : (
                        <button
                          key={pos.ticker}
                          onClick={() => handleQuickAddPosition(pos.ticker, pos.direction, pos.rationale)}
                          disabled={addingPosition}
                          title={pos.rationale || pos.ticker}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            background: '#fff', border: '1px solid #d1d5db', borderRadius: 8,
                            padding: '6px 12px', cursor: addingPosition ? 'not-allowed' : 'pointer',
                            fontSize: '0.8rem', fontFamily: 'inherit', transition: 'all 0.15s',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#22c55e'; e.currentTarget.style.background = '#f0fdf4'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.background = '#fff'; }}
                        >
                          <span style={{
                            fontSize: '0.65rem', fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                            background: pos.direction === 'long' ? '#dcfce7' : '#fee2e2',
                            color: pos.direction === 'long' ? '#16a34a' : '#dc2626',
                          }}>
                            {pos.direction === 'long' ? 'LONG' : 'SHORT'}
                          </span>
                          <span style={{ fontWeight: 600, color: '#111827' }}>{pos.ticker}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: 0 }}>
                      {extracted.length > 0 ? 'All detected positions already added.' : 'No tickers detected in this insight.'}
                    </p>
                  )}
                  {insightPositions.length > 0 && (
                    <div style={{ marginTop: 12, borderTop: '1px solid #bbf7d0', paddingTop: 10 }}>
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 6 }}>Already tracked:</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {insightPositions.map((p: any) => (
                          <span key={p.id} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
                            padding: '3px 8px', fontSize: '0.75rem', color: '#374151',
                          }}>
                            <span style={{
                              color: p.direction === 'long' ? '#16a34a' : '#dc2626',
                              fontWeight: 600,
                            }}>
                              {p.direction === 'long' ? 'L' : 'S'}
                            </span>
                            {p.ticker} @ ${p.entry_price?.toFixed(2)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

          {/* Refine feedback panel */}
          {showRefine && !isEditing && (
            <div style={{
              background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
              padding: 16, marginBottom: 16,
            }}>
              <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: 8 }}>
                Give feedback — the AI will revise the essay based on your input:
              </p>
              <textarea
                value={refineText}
                onChange={(e) => setRefineText(e.target.value)}
                placeholder="e.g., The gold analysis misses central bank buying. Shorten the energy section. Add more on rate transmission to housing."
                rows={3}
                style={{
                  width: '100%', padding: '8px 12px', background: '#fff',
                  border: '1px solid #d1d5db', borderRadius: 8,
                  color: '#1f2937', fontSize: '0.85rem', resize: 'vertical',
                  fontFamily: 'inherit', boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button
                  onClick={() => { setShowRefine(false); setRefineText(''); }}
                  style={{
                    background: '#fff', border: '1px solid #d1d5db',
                    borderRadius: 6, padding: '6px 14px', cursor: 'pointer',
                    color: '#374151', fontSize: '0.8rem',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRefine}
                  disabled={refining || !refineText.trim()}
                  style={{
                    background: refining ? '#9ca3af' : '#3b82f6', border: 'none',
                    borderRadius: 6, padding: '6px 14px', cursor: refining ? 'not-allowed' : 'pointer',
                    color: '#fff', fontSize: '0.8rem', fontWeight: 600,
                  }}
                >
                  {refining ? 'Refining...' : 'Refine Essay'}
                </button>
              </div>
            </div>
          )}

          {/* Row 2: Copy, PDF, Share, Publish (only in preview mode) */}
          {!isEditing && (
            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
              <button
                onClick={handleCopy}
                style={{
                  background: '#fff', border: '1px solid #d1d5db',
                  borderRadius: 8, padding: '7px 12px', cursor: 'pointer', color: '#374151',
                  fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 5
                }}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? 'Copied' : 'Copy'}
              </button>
              <button
                onClick={handlePDF}
                style={{
                  background: '#fff', border: '1px solid #d1d5db',
                  borderRadius: 8, padding: '7px 12px', cursor: 'pointer', color: '#374151',
                  fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 5
                }}
              >
                <Download size={14} /> PDF
              </button>
              <button
                onClick={handleShare}
                style={{
                  background: '#fff', border: '1px solid #d1d5db',
                  borderRadius: 8, padding: '7px 12px', cursor: 'pointer', color: '#374151',
                  fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 5
                }}
              >
                <Share2 size={14} /> Share
              </button>
              <button
                onClick={handlePublish}
                style={{
                  background: viewingInsight?.status === 'published' ? '#10b981' : '#3b82f6',
                  border: 'none',
                  borderRadius: 8, padding: '7px 12px', cursor: 'pointer', color: '#fff',
                  fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 5
                }}
              >
                <Globe size={14} /> {viewingInsight?.status === 'published' ? 'Published' : 'Publish'}
              </button>
            </div>
          )}
        </div>

        <style>{`
          .ql-editor { font-family: inherit; font-size: 0.95rem; line-height: 1.8; color: #374151; }
          .ql-editor p { margin-bottom: 1em; }
          .ql-editor img { max-width: 100%; height: auto; display: block; margin: 8px 0; cursor: pointer; }
          .insight-html-preview p { margin-bottom: 1em; }
          .insight-html-preview h2 { font-size: 1.25rem; font-weight: 700; margin: 1.2em 0 0.5em; }
          .insight-html-preview h3 { font-size: 1.1rem; font-weight: 600; margin: 1em 0 0.4em; }
        `}</style>

        {isEditing ? (
          /* Edit mode */
          <>
            <input
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              style={{
                width: '100%', fontSize: '1.5rem', fontWeight: 700, color: '#111827',
                border: '1px solid #d1d5db', borderRadius: 8, padding: '12px 16px',
                marginBottom: 16, boxSizing: 'border-box', background: '#fff',
              }}
            />
            <div style={{
              background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb',
              overflow: 'hidden',
              maxHeight: 'calc(100vh - 250px)',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <ReactQuill
                theme="snow"
                defaultValue={editContent}
                onChange={(val: string) => { editContentRef.current = val; }}
                style={{ minHeight: 300, flex: 1, overflow: 'auto' }}
                modules={{
                  toolbar: [
                    [{ header: [2, 3, false] }],
                    ['bold', 'italic', 'underline'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['blockquote'],
                    ['link', 'image'],
                    ['clean'],
                  ],
                }}
              />
            </div>
          </>
        ) : (
          /* Preview mode — interleave charts with paragraphs */
          <>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: 16, color: '#111827' }}>
              {viewingInsight.title}
            </h1>
            <div style={{
              lineHeight: 1.8, color: '#374151', fontSize: '0.95rem',
              background: '#fff', borderRadius: 12, padding: 24,
              border: '1px solid #e5e7eb'
            }}>
              {viewingInsight.content?.includes('<p>') || viewingInsight.content?.includes('<h') ? (
                <div
                  dangerouslySetInnerHTML={{ __html: viewingInsight.content }}
                  style={{ whiteSpace: 'normal' }}
                  className="insight-html-preview"
                />
              ) : (() => {
                // Match charts to paragraphs by keyword relevance
                const charts: ChartSpec[] = viewingInsight.charts || [];
                const chartKeywords: Record<string, string[]> = {};
                charts.forEach((c: ChartSpec, idx: number) => {
                  const title = (c.title || '').toLowerCase();
                  const keys: string[] = [];
                  if (title.includes('vix')) keys.push('vix', 'volatility', 'fear');
                  if (title.includes('spy')) keys.push('spy', 's&p', 'broad market', 'index');
                  if (title.includes('sector')) keys.push('sector', 'xle', 'xlk', 'xlf', 'energy', 'technology', 'rotation');
                  if (title.includes('price') && !title.includes('sector')) keys.push(title.split(' ')[0]); // ticker name
                  chartKeywords[String(idx)] = keys;
                });

                const usedCharts = new Set<number>();
                const elements: any[] = [];

                paragraphs.forEach((para: string, i: number) => {
                  // Render paragraph
                  const parts = para.split(/(\*\*[^*]+\*\*)/g);
                  elements.push(
                    <p key={`p-${i}`} style={{ marginBottom: '1em' }}>
                      {parts.map((part: string, j: number) => {
                        if (part.startsWith('**') && part.endsWith('**')) {
                          return <strong key={j}>{part.slice(2, -2)}</strong>;
                        }
                        return <span key={j}>{part}</span>;
                      })}
                    </p>
                  );

                  // Check if any chart matches this paragraph
                  const paraLower = para.toLowerCase();
                  charts.forEach((chart: ChartSpec, cidx: number) => {
                    if (usedCharts.has(cidx)) return;
                    const keys = chartKeywords[String(cidx)] || [];
                    const matches = keys.filter(k => paraLower.includes(k));
                    if (matches.length >= 1) {
                      elements.push(
                        <div key={`c-${cidx}`} style={{ margin: '16px 0' }}>
                          <InsightChart chart={chart} />
                        </div>
                      );
                      usedCharts.add(cidx);
                    }
                  });
                });

                // Append any remaining unmatched charts at the end
                charts.forEach((chart: ChartSpec, cidx: number) => {
                  if (!usedCharts.has(cidx)) {
                    elements.push(
                      <div key={`c-end-${cidx}`} style={{ margin: '16px 0' }}>
                        <InsightChart chart={chart} />
                      </div>
                    );
                  }
                });

                return elements;
              })()}
            </div>

            {/* Trade Action Buttons — detect tickers and offer quick navigation */}
            {!isEditing && (() => {
              const content = viewingInsight.content || '';
              // Detect ticker symbols: 1-5 uppercase letters that appear as known patterns
              const knownTickers = new Set([
                'AAPL','MSFT','NVDA','TSLA','AMZN','GOOGL','GOOG','META','JPM','GS',
                'BAC','XOM','CVX','COP','UNH','JNJ','PFE','LLY','BA','CAT',
                'GLD','SLV','USO','GDX','XME','DBA','UNG','COPX','TLT','HYG',
                'XLE','XLK','XLF','XLV','XLI','XLU','XLY','XLB','XLC','XLRE',
                'SPY','QQQ','IWM','DIS','NFLX','CRM','COIN','PLTR','ARM','AMD',
                'INTC','MU','DELL','SMCI','AVGO','TSM','QCOM','TXN',
              ]);
              const found: string[] = [];
              const words: string[] = content.replace(/[^A-Z]/g, ' ').split(/\s+/);
              words.forEach(w => {
                if (knownTickers.has(w) && !found.includes(w)) found.push(w);
              });

              if (found.length === 0) return null;

              const navigateTo = (view: string, data?: any) => {
                if (data?.ticker) localStorage.setItem('aiq_navigate_ticker', data.ticker);
                if (data?.pairs) localStorage.setItem('aiq_navigate_pairs', JSON.stringify(data.pairs));
                localStorage.setItem('aiq_active_view', view);
                // Dispatch storage event so AiQApp picks up the view change
                window.dispatchEvent(new StorageEvent('storage', { key: 'aiq_active_view', newValue: view }));
              };

              return (
                <div style={{ marginTop: 20, padding: '16px 20px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: 10, fontWeight: 500 }}>
                    Tickers mentioned — click to analyze:
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {found.slice(0, 12).map(ticker => (
                      <button
                        key={ticker}
                        onClick={() => navigateTo('STOCK_ANALYSIS', { ticker })}
                        style={{
                          padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
                          fontSize: '0.8rem', fontWeight: 600, fontFamily: 'monospace',
                          border: '1px solid #d1d5db', background: '#fff', color: '#1f2937',
                        }}
                      >
                        {ticker}
                      </button>
                    ))}
                  </div>
                  {found.length >= 2 && (
                    <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <span style={{ fontSize: '0.75rem', color: '#9ca3af', marginRight: 4, lineHeight: '28px' }}>Pairs:</span>
                      {found.slice(0, 4).flatMap((t1, i) =>
                        found.slice(i + 1, i + 3).map(t2 => (
                          <button
                            key={`${t1}-${t2}`}
                            onClick={() => navigateTo('BACKTEST_PAIRS', { pairs: { asset1: t1, asset2: t2 } })}
                            style={{
                              padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
                              fontSize: '0.75rem', border: '1px solid #d1d5db',
                              background: '#fff', color: '#374151',
                            }}
                          >
                            {t1} / {t2}
                          </button>
                        ))
                      ).slice(0, 6)}
                    </div>
                  )}
                </div>
              );
            })()}
          </>
        )}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px', height: '100vh', overflow: 'auto', paddingBottom: 100 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <IconPenTool />
        <h1 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#111827', margin: 0 }}>Market Insights</h1>
      </div>

      {/* Generator */}
      <div style={{
        background: '#fff', borderRadius: 12,
        border: '1px solid #e5e7eb', padding: 20, marginBottom: 24,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
      }}>

        {/* Step 1: Choose insight type */}
        <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 8, fontWeight: 500 }}>
          What do you want to analyze?
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8, marginBottom: 16 }}>
          {INSIGHT_PRESETS.map(preset => (
            <button
              key={preset.key}
              onClick={() => setSelectedPreset(preset.key)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px',
                background: selectedPreset === preset.key ? '#eff6ff' : '#f9fafb',
                border: `1px solid ${selectedPreset === preset.key ? '#3b82f6' : '#e5e7eb'}`,
                borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                transition: 'all 0.15s',
              }}
            >
              <div style={{ color: selectedPreset === preset.key ? '#3b82f6' : '#9ca3af', flexShrink: 0, marginTop: 2 }}>
                {preset.icon}
              </div>
              <div>
                <div style={{ color: '#111827', fontSize: '0.85rem', fontWeight: 600 }}>{preset.label}</div>
                <div style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: 2, lineHeight: 1.3 }}>{preset.description}</div>
              </div>
            </button>
          ))}
        </div>

        {/* Step 2: Context inputs (conditional on preset) */}
        {activePreset && (
          <>
            {/* Ticker input */}
            {(activePreset.needsTickers || selectedPreset === 'custom') && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  {activePreset.needsTickers ? 'Tickers' : 'Tickers (optional)'}
                </label>
                <input
                  value={tickerInput}
                  onChange={(e) => setTickerInput(e.target.value.toUpperCase())}
                  placeholder="AAPL, MSFT, NVDA"
                  style={{
                    width: '100%', padding: '8px 12px', background: '#fff',
                    border: '1px solid #d1d5db', borderRadius: 8,
                    color: '#1f2937', fontSize: '0.85rem', boxSizing: 'border-box',
                  }}
                />
              </div>
            )}

            {/* Sector info (ETF + name) for Sector Deep Dive */}
            {activePreset.needsSectorInfo && (
              <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                <div style={{ flex: '1 1 120px' }}>
                  <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                    Sector ETF (optional)
                  </label>
                  <input
                    value={sectorEtf}
                    onChange={(e) => setSectorEtf(e.target.value.toUpperCase())}
                    placeholder="e.g. XLF, XLK, SMH"
                    style={{
                      width: '100%', padding: '8px 12px', background: '#fff',
                      border: '1px solid #d1d5db', borderRadius: 8,
                      color: '#1f2937', fontSize: '0.85rem', boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div style={{ flex: '2 1 200px' }}>
                  <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                    Sector / Theme Name
                  </label>
                  <input
                    value={sectorName}
                    onChange={(e) => setSectorName(e.target.value)}
                    placeholder="e.g. Large-Cap Banking, Photonics / Optical Networks"
                    style={{
                      width: '100%', padding: '8px 12px', background: '#fff',
                      border: '1px solid #d1d5db', borderRadius: 8,
                      color: '#1f2937', fontSize: '0.85rem', boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>
            )}

            {/* Portfolio selector */}
            {(activePreset as any).needsPortfolio && portfolios.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Portfolio
                </label>
                <select
                  value={selectedPortfolio}
                  onChange={(e) => setSelectedPortfolio(e.target.value)}
                  style={{
                    width: '100%', padding: '8px 10px', background: '#fff',
                    border: '1px solid #d1d5db', borderRadius: 8,
                    color: '#1f2937', fontSize: '0.85rem',
                  }}
                >
                  {portfolios.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Custom notes / title — context depends on preset */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                {(activePreset as any).isManual ? 'Draft Title' : (activePreset as any).needsCustom ? 'Your Thesis / Notes' : 'Additional Notes (optional)'}
              </label>
              {(activePreset as any).isManual ? (
                <input
                  value={customText}
                  onChange={(e) => setCustomText(e.target.value)}
                  placeholder="e.g. Q1 2026 Market Outlook"
                  style={{
                    width: '100%', padding: '8px 12px', background: '#fff',
                    border: '1px solid #d1d5db', borderRadius: 8,
                    color: '#1f2937', fontSize: '0.85rem', boxSizing: 'border-box',
                  }}
                />
              ) : (
                <textarea
                  value={customText}
                  onChange={(e) => setCustomText(e.target.value)}
                  placeholder={(activePreset as any).needsCustom
                    ? "Write your thesis, observations, or analysis notes..."
                    : "Add context or angle you want the insight to focus on..."
                  }
                  rows={3}
                  style={{
                    width: '100%', padding: '8px 12px', background: '#fff',
                    border: '1px solid #d1d5db', borderRadius: 8,
                    color: '#1f2937', fontSize: '0.85rem', resize: 'vertical',
                    fontFamily: 'inherit', boxSizing: 'border-box',
                  }}
                />
              )}
            </div>

            {/* Format, Model, Style — hidden for manual draft */}
            {!(activePreset as any).isManual && <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
              <div style={{ flex: '1 1 140px' }}>
                <label style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: 3, fontWeight: 500 }}>Format</label>
                <select value={selectedFormat} onChange={(e) => setSelectedFormat(e.target.value)}
                  style={{ width: '100%', padding: '7px 8px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, color: '#1f2937', fontSize: '0.8rem' }}>
                  {FORMAT_OPTIONS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
              </div>
              <div style={{ flex: '1 1 140px' }}>
                <label style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: 3, fontWeight: 500 }}>Model</label>
                <select value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)}
                  style={{ width: '100%', padding: '7px 8px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, color: '#1f2937', fontSize: '0.8rem' }}>
                  {MODEL_OPTIONS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
                </select>
              </div>
              <div style={{ flex: '1 1 140px' }}>
                <label style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: 3, fontWeight: 500 }}>Style</label>
                <select value={selectedStyle} onChange={(e) => setSelectedStyle(e.target.value)}
                  style={{ width: '100%', padding: '7px 8px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, color: '#1f2937', fontSize: '0.8rem' }}>
                  <option value="">Default (neutral)</option>
                  {Object.entries(styles).map(([key, s]) => (
                    <option key={key} value={key}>{s.label}{s.sample_count && s.sample_count > 1 ? ` (${s.sample_count})` : ''}</option>
                  ))}
                </select>
              </div>
            </div>}

            {/* Generate */}
            <button
              onClick={handleGenerate}
              disabled={generating}
              style={{
                width: '100%', padding: '12px 20px',
                background: generating ? '#9ca3af' : 'linear-gradient(135deg, #3b82f6, #6366f1)',
                border: 'none', borderRadius: 10, cursor: generating ? 'not-allowed' : 'pointer',
                color: '#fff', fontSize: '0.9rem', fontWeight: 600,
                opacity: generating ? 0.7 : 1,
              }}
            >
              {generating ? (selectedPreset === 'manual' ? 'Creating...' : 'Generating...') : (selectedPreset === 'manual' ? 'Create Draft' : 'Generate Insight')}
            </button>
          </>
        )}
      </div>

      {/* Saved Insights */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: '#111827', margin: 0 }}>
            <FileText size={16} style={{ verticalAlign: -3, marginRight: 6 }} />
            Saved Insights
          </h3>
          <button
            onClick={async () => {
              const t = await getToken();
              if (!t) return;
              try {
                const data = await getMyInsights(t);
                setSavedInsights(data.insights || []);
                toast.success('Refreshed');
              } catch { toast.error('Failed to refresh'); }
            }}
            style={{
              background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
              padding: '4px 10px', cursor: 'pointer', color: '#6b7280',
              fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 4
            }}
          >
            Refresh
          </button>
        </div>

        {savedInsights.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '40px 20px', color: '#9ca3af',
            background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb',
          }}>
            <FileText size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
            <p>No insights yet. Generate your first one above.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {savedInsights.map(insight => (
              <div
                key={insight.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                  background: '#fff', borderRadius: 10,
                  border: '1px solid #e5e7eb', cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                }}
                onClick={() => handleViewInsight(insight.id)}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#111827', fontSize: '0.9rem', fontWeight: 500 }}>
                    {insight.title}
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginTop: 4 }}>
                    {insight.format} · {insight.sources_used?.join(', ')} · {insight.created_at ? new Date(insight.created_at).toLocaleDateString() : ''}
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteInsight(insight.id); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}
                  title="Delete"
                >
                  <IconTrash />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
